"use client";

import { omittedApplicationScopeFields } from "../application-scope";
import { DocumentBody } from "@/features/service-information/DocumentBody";
import { LicenseScopeDescription } from "@/features/platform-license/LicenseScopeDescription";
import { agreement, fillAgreementAppendix, type DocumentBlock } from "../agreement-document";
import { useId } from "react";
import { AUTHORIZATION_SCOPE_FIELDS, scopeFieldVisible, type AuthorizationScope } from "../authorization-scope";

export function AuthorizationScopeFields({ scope, onChange }: { scope: AuthorizationScope; onChange: (scope: AuthorizationScope) => void }) {
  const id = useId();
  const update = (key: string, value: string | string[]) => onChange({ version: 1, values: {
    ...scope.values, [key]: key === "actions" && Array.isArray(value) ? value.join("、") : value,
  } });
  const aiFields = AUTHORIZATION_SCOPE_FIELDS.filter(field => field.key === "ai" || field.key === "aiOther");
  const fields = AUTHORIZATION_SCOPE_FIELDS.filter(field => field.key !== "ai" && field.key !== "aiOther")
    .flatMap(field => field.key === "region" ? [field, ...aiFields] : [field]);
  const controlClass = "w-full rounded-[var(--radius-input)] border border-[var(--color-line)] bg-[var(--color-sunken)] px-3 py-2.5 text-sm font-normal text-[var(--color-paper)]";
  return <div className="grid gap-4">
    <p className="text-xs leading-5 text-[var(--color-muted)]">以下为本次申请范围，须由授权方确认；付款不代表授权生效。</p>
    {fields.filter(field => !omittedApplicationScopeFields.has(field.key) && scopeFieldVisible(field, scope.values)).map(field => {
      const rawValue = scope.values[field.key];
      const value = field.key === "actions" && typeof rawValue === "string"
        ? rawValue.split(/[、，,；;\n]+/u).map(item => item.trim()).filter(Boolean) : rawValue;
      const options = field.key === "actions"
        ? [...new Set(["复制", "剪辑", "展示", "投放", "传播", ...(Array.isArray(value) ? value : [])])]
        : field.key === "region" ? ["中国", "泰国", "韩国"] : field.options;
      const multiple = field.key === "actions" || field.multiple;
      if (options) return <fieldset key={field.key} data-scope-field={field.key} className="grid gap-2">
        <legend className="mb-2 text-sm font-medium">{field.label}</legend>
        {field.hint && field.key !== "actions" && <p className="text-xs text-[var(--color-muted)]">{field.hint}</p>}
        <div className="flex flex-wrap gap-x-5 gap-y-2">{options.map(option => <label key={option} className="inline-flex items-center gap-2 text-sm">
          <input type={multiple ? "checkbox" : "radio"} name={`${id}-${field.key}`} className="h-4 w-4 accent-[var(--color-accent)]"
            checked={Array.isArray(value) ? value.includes(option) : value === option}
            onChange={event => {
              if (!multiple) { update(field.key, option); return; }
              const previous = Array.isArray(value) ? value : [];
              update(field.key, !event.target.checked ? previous.filter(item => item !== option)
                : option === "不允许" ? [option] : [...previous.filter(item => item !== "不允许"), option]);
            }} />{option}
        </label>)}</div>
      </fieldset>;
      return <label key={field.key} data-scope-field={field.key} className="grid gap-1.5 text-sm font-medium">
        {field.label}{field.optional ? "（选填）" : ""}
        {field.date || field.key === "materialId"
          ? <input className={controlClass} type={field.date ? "date" : "text"} readOnly={field.key === "materialId"} value={typeof value === "string" ? value : ""} maxLength={500} onChange={event => update(field.key, event.target.value)} />
          : <textarea className={controlClass} rows={2} maxLength={500} placeholder={field.key === "character" ? "简要说明" : field.key === "providers" ? "模型／制作服务商名称" : field.key === "purpose" ? undefined : field.hint} value={typeof value === "string" ? value : ""} onChange={event => update(field.key, event.target.value)} />}
      </label>;
    })}
  </div>;
}

export function AuthorizationScopeSummary({ scope, usage, term, blocks }: { scope: AuthorizationScope; usage?: string; term?: string; blocks?: DocumentBlock[] }) {
  const document = blocks ?? fillAgreementAppendix(agreement.blocks, scope, { usage, term });
  const start = document.findIndex(block => block.runs?.map(run => run.text).join("").includes("附件：授权范围"));
  const table = document.slice(start < 0 ? 0 : start).find(block => block.type === "table");
  return <section aria-label="申请授权范围" className="mt-4 text-sm">
    <h3 className="font-semibold">附件：授权范围</h3>
    {table && <DocumentBody blocks={[table]} renderRuns={runs => runs.map(run => run.text).join("") === "详见《数字形象授权范围说明》" ? <>详见<LicenseScopeDescription /></> : undefined} />}
  </section>;
}
